import React, {
	useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import * as Comlink from 'comlink';
/* eslint-disable import/no-webpack-loader-syntax */
import SearchWorker from 'worker-loader!../../../workers/search-worker';

import { Channel } from '../../../stores/reducers/home';
import { APP } from '../../../stores/session-status';
import { EAsyncStatus, GreenroomState as GS } from '../../../stores/types/types';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import CustomSelectGroup from '../../general-components/custom-select-group';
import ChannelAvatar from '../../general-components/channel-avatar';
import {
	CopyMeeting,
	CreateOrUpdateMeeting,
	LoadMeetingsEventsLegacyProducerByChannel,
	LoadMeetingsEventsProducer,
	LoadMeetingsStandaloneGreenroomProducerByChannel,
} from '../../../connection/meeting';
import { setModalOpen } from '../../../stores/actions/modal';
import { setHomeChannel } from '../../../stores/actions/home';
import WaitingIndicator from '../../general-components/waiting-indicator';
import useHome from '../../../hooks/use-home';
import {
	dayFirstStartEndDate,
	dbDateToObject,
	getChannelLogoFromMeeting,
	getImageLocation,
	truncateTextBeforeSpace,
} from '../../../utils/utils';

let searchWorker: any;

(async function init() {
	const worker = new SearchWorker();
	searchWorker = Comlink.wrap(worker) as any;
}());

/*
These could be integrated into the main component, but it feels more readable this way.
They could also be extracted to their own component files, but right now they aren't
being used anywhere else.
*/
interface IChannelOptionProps {
	selectedChannel?: Channel;
	onClick?: (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
		targetChannel: Channel
	) => void;
	topOption?: boolean;
	currentChannelId?: number;
}

const ChannelOption = ({
	selectedChannel,
	onClick,
	topOption = false,
	currentChannelId = 0,
}: IChannelOptionProps): JSX.Element => {
	if (!selectedChannel || !onClick) {
		if (topOption) {
			return (
				<div
					className={classNames('channel-option first-option', {
						selected: currentChannelId === 0,
					})}
				>
					<div className="image-container">
						<div className="background gray">
							<div className="question-bg">
								<Icon
									name={ICONS.QUESTION}
									size={14}
									color={COLORS.PLACEHOLDER_GRAY}
								/>
							</div>
						</div>
					</div>
					<div className="title">Select Channel</div>
				</div>
			);
		}
		return <></>;
	}

	const logo = useMemo(() => getImageLocation(getChannelLogoFromMeeting(selectedChannel)) ?? '', [selectedChannel]);

	return (
		<div
			className={classNames('channel-option', {
				selected: currentChannelId === selectedChannel.channel,
			})}
			onClick={(event) => onClick(event, selectedChannel)}
		>
			<div className="image-container">
				<ChannelAvatar
					channelName={selectedChannel.name}
					logoWhite={logo}
				/>
			</div>
			<div className="title">{selectedChannel.name}</div>
		</div>
	);
};

interface IEventOptionProps {
	selectedEvent?: { [key: string]: any };
	selectedEventIndex?: number;
	onClick?: (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
		targetEvent: { [key: string]: any }
	) => void;
	topOption?: boolean;
	currentEventId?: number;
}

const EventOption = ({
	selectedEvent,
	selectedEventIndex,
	onClick,
	topOption = false,
	currentEventId = 0,
}: IEventOptionProps): JSX.Element => {
	if (!selectedEvent || !onClick) {
		if (topOption) {
			return (
				<div className="event-option-container">
					<div
						className={classNames('event-option first-option', {
							selected: currentEventId === selectedEventIndex,
						})}
					>
						<input type="text" defaultValue={selectedEvent?.title ?? ''} />
					</div>
				</div>
			);
		}
		return <></>;
	}

	const start = dbDateToObject(selectedEvent.start_time);
	const end = dbDateToObject(selectedEvent.end_time);
	const dateString = dayFirstStartEndDate(start, end, 'Date missing from show');
	const { firstPastEvent, firstUpcomingEvent } = selectedEvent;

	return (
		<div className={classNames('event-option-container', {
			'first-past': firstPastEvent,
			'first-upcoming': firstUpcomingEvent,
		})}>
			<div
				className={classNames('event-option title-date', {
					selected: currentEventId === selectedEventIndex,
				})}
				onClick={(event) => onClick(event, selectedEvent)}
			>
				<h6 className="title">{selectedEvent.title}</h6>
				<div className="date">{dateString}</div>
			</div>
		</div>
	);
};

interface ICopyEventProps {
	sourceMeeting: { [key: string]: any };
}

const COPY_MODAL_PAGES = {
	TARGET_EVENT_SELECTION: 1,
	INFO_PAGE: 2,
	SUCCESS_PAGE: 3,
};

export default function CopyEvent({ sourceMeeting }: ICopyEventProps): JSX.Element {
	const dispatch = useDispatch();
	const appSignedInWith = useSelector((s: GS) => s.SessionReducer.appSignedInWith);
	const eventsChannels = useSelector((s: GS) => s.SessionReducer.eventsChannels);
	const eventsLegacyChannels = useSelector((s: GS) => s.SessionReducer.eventsLegacyChannels);
	const grChannels = useSelector((s: GS) => s.SessionReducer.grChannels);
	const selectedChannel = useSelector((s: GS) => s.HomeReducer.selectedChannel);
	const token = useSelector((s: GS) => s.SessionReducer.token);

	// Channels State
	const [channels, setChannels] = useState<Channel[]>([]);
	const [channelIndex, setChannelIndex] = useState<number | undefined>();
	const [channelsOpen, setChannelsOpen] = useState(false);

	// Events State
	const [events, setEvents] = useState<{ [key: string]: any }[]>([]);
	const [filteredEvents, setFilteredEvents] = useState<{ [key: string]: any }[]>([]);
	const [eventIndex, setEventIndex] = useState<number | undefined>();
	const [eventsOpen, setEventsOpen] = useState(false);
	const [
		validatedSourceEvent,
		setValidatedSourceEvent,
	] = useState<{ [key: string]: any } | undefined>();
	const [selectedEventTitle, setSelectedEventTitle] = useState('');

	// General / Page State
	const [page, setPage] = useState(COPY_MODAL_PAGES.TARGET_EVENT_SELECTION);
	const [status, setStatus] = useState(EAsyncStatus.Incomplete);

	// Refs
	const searchDebounce = useRef<NodeJS.Timeout | null>(null);

	const { handleEnterShowDetails } = useHome();

	// Help Article Link
	const helpLink = 'https://help.brand.live/hc/en-us/articles/4412914576407-Copying-a-Greenroom';

	// List of items being copied
	const copyList = [
		'Showboard',
		'Design',
		'Media',
		'Notes & Shot Notes',
		'Settings',
		'Overlays',
	];

	useEffect(() => {
		if (!channels.length) {
			switch (appSignedInWith) {
				case APP.EVENTS:
					setChannels(eventsChannels);
					break;
				case APP.EVENTS_LEGACY:
					setChannels(eventsLegacyChannels);
					break;
				case APP.GREENROOM:
					setChannels(grChannels);
					break;
				default:
					break;
			}
		}
	}, [appSignedInWith, channels.length, eventsChannels, eventsLegacyChannels, grChannels]);

	const currentChannelId = useMemo((): number => (channelIndex !== undefined
		? channels?.[channelIndex]?.channel ?? 0
		: 0), [channelIndex, channels]);

	const channelElements = useMemo(() => {
		const changeChannel = (
			event: React.MouseEvent<HTMLDivElement, MouseEvent>,
			targetChannel: Channel,
		) => {
			const selectedChannelIndex = channels.findIndex(
				(channel) => channel.channel === targetChannel.channel,
			);

			setChannelIndex(selectedChannelIndex);
		};

		return channels.map((channel) => (
			<ChannelOption
				key={channel.channel}
				selectedChannel={channel}
				onClick={(e) => changeChannel(e, channel)}
				currentChannelId={currentChannelId}
			/>
		));
	}, [channels, currentChannelId]);

	const filteredEventsUpcomingPast = useMemo((): { [key: string]: any }[] => {
		const past = filteredEvents?.filter(
			(event: any) => new Date(event.start_time) < new Date(),
		);
		const upcoming = filteredEvents?.filter(
			(event: any) => new Date(event.start_time) >= new Date(),
		);

		if (past[0]) past[0].firstPastEvent = true;
		if (upcoming[0]) upcoming[0].firstUpcomingEvent = true;

		return [...upcoming, ...past];
	}, [filteredEvents]);

	const eventElements = useMemo((): JSX.Element[] => {
		const changeEvent = (
			event: React.MouseEvent<HTMLDivElement, MouseEvent>,
			targetEventIndex: number,
		) => {
			setEventIndex(targetEventIndex);
			setSelectedEventTitle(filteredEventsUpcomingPast?.[targetEventIndex]?.title ?? 'Selected Show');
		};

		const allFilteredElements: JSX.Element[] = filteredEventsUpcomingPast.map(
			(event: any, index: number) => (
				<EventOption
					key={index}
					selectedEvent={event}
					selectedEventIndex={eventIndex}
					onClick={(e) => changeEvent(e, index)}
					currentEventId={index}
				/>
			),
		);

		return allFilteredElements.length
			? allFilteredElements
			: [
				<div key={0} className="event-option-container">
					<div className="event-option">
						<h6 className="title no-margin">No shows available</h6>
					</div>
				</div>,
			];
	}, [eventIndex, filteredEventsUpcomingPast]);

	/*
	`sourceMeeting` comes from multiple sources and may not always have
	all of the correct object keys. In the long term, this is a refactoring
	opportunity. For now, this can be remedied by looking up the event
	by channel if it is possible to do so.
	*/
	useEffect(() => {
		const loadValidatedEvent = async (
			passedToken: string,
			passedSourceMeeting: { [key: string]: any },
			set: React.Dispatch<React.SetStateAction<any[]>>,
		): Promise<void> => {
			const channelId = passedSourceMeeting?.channel?.channel;

			let foundSourceMeeting;

			if (channelId) {
				switch (appSignedInWith) {
					case APP.EVENTS: {
						const eventsForChannelEvents = await LoadMeetingsEventsProducer(
							passedToken,
							channelId,
						);

						foundSourceMeeting = eventsForChannelEvents.find(
							(event: { [key: string]: any }) => event.events_session_uuid
								=== passedSourceMeeting.events_session_uuid,
						);
						break;
					}
					case APP.EVENTS_LEGACY: {
						const eventsForChannelLegacy = await LoadMeetingsEventsLegacyProducerByChannel(
							passedToken,
							channelId,
						);

						foundSourceMeeting = eventsForChannelLegacy.find(
							(event: { [key: string]: any }) => event.live_chat === passedSourceMeeting.live_chat,
						);
						break;
					}
					case APP.GREENROOM: {
						const eventsForChannelStandalone = await LoadMeetingsStandaloneGreenroomProducerByChannel(
							passedToken,
							channelId,
						);

						foundSourceMeeting = eventsForChannelStandalone.find(
							(event: { [key: string]: any }) => event.meeting === passedSourceMeeting.meeting,
						);
						break;
					}
					default: {
						break;
					}
				}

				if (foundSourceMeeting) {
					set(foundSourceMeeting);
				}
			}
		};

		if (token && sourceMeeting?.channel) {
			loadValidatedEvent(token, sourceMeeting, setValidatedSourceEvent);
		}
	}, [appSignedInWith, sourceMeeting, token]);

	useEffect(() => {
		const loadEvents = async (
			passedToken: string,
			channel: number,
			set: React.Dispatch<React.SetStateAction<any[]>>,
		): Promise<void> => {
			let filterSourceEventKey: string | undefined;

			let eventsForChannel = [];

			switch (appSignedInWith) {
				case APP.EVENTS:
					eventsForChannel = await LoadMeetingsEventsProducer(passedToken, channel);
					filterSourceEventKey = 'events_session_uuid';
					break;
				case APP.EVENTS_LEGACY:
					eventsForChannel = await LoadMeetingsEventsLegacyProducerByChannel(passedToken, channel);
					filterSourceEventKey = 'live_chat';
					break;
				case APP.GREENROOM:
					eventsForChannel = await LoadMeetingsStandaloneGreenroomProducerByChannel(
						passedToken,
						channel,
					);
					filterSourceEventKey = 'meeting';
					break;
				default:
					break;
			}

			if (Array.isArray(eventsForChannel) && eventsForChannel.length) {
				// Try to exclude the source event so we are not copying an event to itself
				if (filterSourceEventKey?.length) {
					const eventsForChannelNoSource = eventsForChannel.filter(
						(event) => event[filterSourceEventKey ?? '']
							!== sourceMeeting?.[filterSourceEventKey ?? ''],
					);

					set(eventsForChannelNoSource);
				} else {
					set(eventsForChannel);
				}
			}
		};

		if (token && currentChannelId !== 0) {
			loadEvents(token, currentChannelId, setEvents);
		}
	}, [appSignedInWith, currentChannelId, sourceMeeting, token]);

	// Load Filtered Events
	useEffect(() => {
		if (events) {
			searchWorker.sort('time-desc', events).then(async () => {
				setFilteredEvents(await searchWorker.results);
			});
		}
	}, [events]);

	const searchEvents = useCallback((value: string) => {
		searchDebounce.current = setTimeout(() => {
			searchWorker.search(value, events, 'time-desc').then(async () => {
				setFilteredEvents(await searchWorker.results);
			});
		}, 250);
	}, [events]);

	// Update Filtered Events
	const filterEvents = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { target: { value } } = e;

		setSelectedEventTitle(value);

		if (!events) {
			return;
		} if (searchDebounce.current) {
			clearTimeout(searchDebounce.current);
		}

		searchEvents(value);
	};

	// Reset search / item selected when show menu opened
	useEffect(() => {
		if (eventsOpen) {
			setSelectedEventTitle('');
			searchEvents('');
			setEventIndex(undefined);
		}
	}, [eventsOpen, searchEvents]);

	// Had issues truncating a partial line with CSS, so going the JS route
	// Loop through the string backwards to cut at the first space before the desired length
	const shortTitle = useCallback((input: string, maxLen: number = 45): JSX.Element => {
		const shortString = truncateTextBeforeSpace(input ?? '', maxLen);
		const AfterText = input.length > shortString.length ? <span>&hellip;</span> : <></>;

		return (
			<span title={input}>
				{shortString}
				{AfterText}
			</span>
		);
	}, []);

	const destMeeting = useMemo((): { [key: string]: any } | undefined => {
		if (eventIndex !== undefined) {
			return filteredEventsUpcomingPast?.[eventIndex];
		}
		return undefined;
	}, [eventIndex, filteredEventsUpcomingPast]);

	const getMeetingId = async (
		passedToken: string,
		selectedApp: string,
		meeting: { [key: string]: any },
	): Promise<number> => {
		if (meeting?.meeting) {
			return meeting?.meeting;
		} if (meeting) {
			let appMeetingId = '';

			switch (selectedApp) {
				case APP.EVENTS:
					appMeetingId = meeting.events_session_uuid;
					break;
				case APP.EVENTS_LEGACY:
					appMeetingId = meeting.live_chat;
					break;
				default:
					break;
			}

			if (appMeetingId) {
				// Avoid destructuring error if response is not what is expected
				// i.e. const { meeting: grMeetingsId } = await ...
				const getMeeting = await CreateOrUpdateMeeting(
					passedToken,
					appMeetingId,
					meeting,
					selectedApp,
				);

				if (getMeeting?.meeting) {
					return getMeeting.meeting;
				}
			}
		}

		return 0;
	};

	const copyMeeting = async (
		copySourceMeeting: { [key: string]: any },
		copyDestMeeting: { [key: string]: any },
	): Promise<boolean> => {
		if (token) {
			const fromMeetingId = await getMeetingId(
				token,
				appSignedInWith,
				copySourceMeeting,
			);
			const toMeetingId = await getMeetingId(
				token,
				appSignedInWith,
				copyDestMeeting,
			);

			if (fromMeetingId && toMeetingId) {
				const result = await CopyMeeting(token, fromMeetingId, toMeetingId);
				return Boolean(result);
			}
		}

		return false;
	};

	const setNextPage = useCallback(() => {
		const nextPage = page + 1;
		if (
			nextPage >= COPY_MODAL_PAGES.TARGET_EVENT_SELECTION
			&& nextPage <= COPY_MODAL_PAGES.SUCCESS_PAGE
		) {
			setPage(nextPage);
		}
	}, [page]);

	const nextButtonClicked = async () => {
		if (page === COPY_MODAL_PAGES.INFO_PAGE) {
			if (sourceMeeting && destMeeting) {
				setStatus(EAsyncStatus.Waiting);

				/*
				Prefer, but don't force using validated (looked up) event.
				Seems better to allow it to use `sourceMeeting` if `validatedSourceEvent`
				is not found since doing so doesn't seem to be an issue in tests.
				*/
				const copied = await copyMeeting(validatedSourceEvent ?? sourceMeeting, destMeeting);

				if (!copied) {
					setStatus(EAsyncStatus.Error);
					dispatch(setModalOpen(false));
					return;
				}
				setStatus(EAsyncStatus.Complete);
			}
		}

		setNextPage();
	};

	const enterGreenroomDetails = () => {
		if (destMeeting && typeof channelIndex !== 'undefined' && channels[channelIndex]) {
			dispatch(setHomeChannel(channels[channelIndex]));
			handleEnterShowDetails(destMeeting);
			dispatch(setModalOpen(false));
		}
	};

	const placeHolderText = useMemo(() => {
		if (channelIndex === undefined) {
			return 'Select a channel above';
		}
		return eventsOpen
			? 'Search for a show'
			: 'Select...';
	}, [channelIndex, eventsOpen]);

	return (
		<div className="info-modal copy-event">
			<div className="title-container">
				{page !== COPY_MODAL_PAGES.SUCCESS_PAGE ? (
					<>
						<ChannelAvatar
							channelName={selectedChannel?.name ?? ''}
							logoWhite={getImageLocation(
								(selectedChannel?.logo_white || selectedChannel?.logo_color) ?? '',
							) ?? ''}
							size="small"
						/>
						<h4>
							Copy &ldquo;{shortTitle(sourceMeeting.title)}&rdquo;
							to&hellip;
						</h4>
					</>
				) : (
					<h4 className="extra-room">Copy Complete!</h4>
				)}
			</div>
			{page === COPY_MODAL_PAGES.TARGET_EVENT_SELECTION && (
				<div className="page-one">
					<div className="channel-selector">
						<CustomSelectGroup
							label={'Channel'}
							options={channelElements}
							closedOption={
								channelIndex === undefined ? (
									<div className="closed-option channel">
										<ChannelOption
											topOption={true}
											currentChannelId={currentChannelId}
										/>
									</div>
								) : (
									<div className="closed-option channel">
										{channelElements[channelIndex]}
									</div>
								)
							}
							isOpen={channelsOpen}
							changeIsOpen={setChannelsOpen}
							optionIndex={channelIndex}
						/>
					</div>
					<div
						className={classNames('event-selector', {
							disabled: channelIndex === undefined,
						})}
					>
						<CustomSelectGroup
							label={'Show'}
							options={eventElements}
							closedOption={
								<div className="closed-option event">
									<input
										type="text"
										value={selectedEventTitle}
										onChange={filterEvents}
										placeholder={placeHolderText}
									/>
								</div>
							}
							isOpen={eventsOpen}
							changeIsOpen={setEventsOpen}
							optionIndex={eventIndex}
						/>
					</div>
				</div>
			)}
			{page === COPY_MODAL_PAGES.INFO_PAGE && (
				<div className="page-two">
					<h4>You are about to copy your Greenroom.</h4>
					<div className="text-content">
						<div className="confirm-info">
							Press &ldquo;Confirm&rdquo; to complete your copy.
							Copying your Greenroom will include:
						</div>
						<ul>
							{copyList.map((item) => <li key={item}>{item}</li>)}
						</ul>
					</div>
				</div>
			)}
			{page === COPY_MODAL_PAGES.SUCCESS_PAGE && (
				<div className="page-three">
					<div className="text-content">
						You may navigate to your new Greenroom now.
					</div>
				</div>
			)}
			{page !== COPY_MODAL_PAGES.SUCCESS_PAGE && (
				<div className="more-info">
					<a
						href={helpLink}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => {
							e.currentTarget.blur();
						}}
					>
						Learn more about Copying a Greenroom
					</a>
				</div>
			)}
			<div className="page-action-row">
				<div className="page-status">
					<span className="current-page">{page}</span>
					<span className="out-of">/3</span>
				</div>
				<div className="button-group">
					{page !== COPY_MODAL_PAGES.SUCCESS_PAGE ? (
						<>
							<button
								className="gray"
								onClick={() => dispatch(setModalOpen(false))}
							>
								Cancel
							</button>
							<button
								className={classNames('green', {
									disabled: !sourceMeeting || !destMeeting,
								})}
								onClick={nextButtonClicked}
							>
								{status === EAsyncStatus.Waiting && (
									<WaitingIndicator />
								)}
								{status !== EAsyncStatus.Waiting && (
									page !== COPY_MODAL_PAGES.INFO_PAGE ? (
										<>Continue</>
									) : (
										<>Confirm</>
									)
								)}
							</button>
						</>
					) : (
						<>
							<button
								className="gray"
								onClick={() => dispatch(setModalOpen(false))}
							>
								Later
							</button>
							<button
								className="green"
								onClick={enterGreenroomDetails}
							>
								Continue to New Greenroom
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
